import type {
  AdvancedTriggerSettingsType,
  NotificationConditionType,
  NotificationFieldChangeSettingsType,
  StatusIncludesConditionType,
  NotificationTriggerSettingsType,
  OptionStateChangedTriggerSettingsType,
  RevertedTriggerSettingsType,
  NotificationTriggerType,
} from '@cims-online/shared'
import type {
  Notification,
  NotificationField,
  NotificationStatus,
} from '../types'
import { triggerMetadata } from '../../../utils/triggerMetadata'
import { approvalFieldSettings } from './approvalFieldSettings'

type ReferenceMaps = {
  fields: Map<string, NotificationField>
  statuses: Map<string, NotificationStatus>
}

type NotificationSummaryReferenceData = {
  fields: NotificationField[]
  statuses: NotificationStatus[]
}

type NotificationConfigurationSummary = {
  conditions: NotificationConditionType[]
  recipientCount: number
  template?: {
    deleted: boolean
    name: string
  }
  trigger?: NotificationTriggerType
  triggerSettings: NotificationTriggerSettingsType | null
}

type NotificationTransition =
  | AdvancedTriggerSettingsType['from']
  | AdvancedTriggerSettingsType['to']
  | RevertedTriggerSettingsType['from']
  | RevertedTriggerSettingsType['to']
type NotificationTransitionState = Exclude<
  NotificationTransition,
  'any_valid'
>[number]
type NotificationStatusState =
  | NotificationTransitionState
  | StatusIncludesConditionType['statuses'][number]

type NotificationRuleClauses = {
  conditions: string[]
  trigger: string
}

function assertNever(value: never): never {
  throw new Error(
    `Unsupported notification trigger settings: ${JSON.stringify(value)}`
  )
}

function toReferenceMaps({
  fields,
  statuses,
}: NotificationSummaryReferenceData): ReferenceMaps {
  return {
    fields: new Map(fields.map((field) => [field.id, field])),
    statuses: new Map(statuses.map((status) => [status.id, status])),
  }
}

function joinList(items: string[], conjunction: 'and' | 'or' = 'or') {
  if (items.length <= 1) return items[0] ?? ''
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, ${conjunction} ${
    items[items.length - 1]
  }`
}

function capitalizeFirst(value: string) {
  return `${value[0]?.toUpperCase() ?? ''}${value.slice(1)}`
}

function referenceLabel(label: string, deleted: boolean) {
  return deleted ? `${label} (Inactive)` : label
}

function fieldName(fieldId: string, { fields }: ReferenceMaps) {
  const field = fields.get(fieldId)
  if (!field) return `Unknown field (${fieldId})`
  return referenceLabel(field.name, field.deleted)
}

function approvalStateLabel(fieldId: string, references: ReferenceMaps) {
  const field = references.fields.get(fieldId)
  if (!field || field.settings.type !== 'approvals')
    return fieldName(fieldId, references)

  const { singular } = approvalFieldSettings(field)
  return referenceLabel(singular, field.deleted)
}

function statusName(statusId: string, { statuses }: ReferenceMaps) {
  const status = statuses.get(statusId)
  if (!status) return `Unknown status (${statusId})`
  return referenceLabel(status.name, status.deleted)
}

function fieldOptionLabel({
  fieldId,
  optionId,
  references,
}: {
  fieldId: string
  optionId: string
  references: ReferenceMaps
}) {
  const field = references.fields.get(fieldId)
  if (field?.settings.type !== 'approvals')
    return `Unknown option (${optionId})`

  const option = field.settings.options.find(({ id }) => id === optionId)
  if (!option) return `Unknown option (${optionId})`
  return referenceLabel(option.label, !!field.deleted || !!option.deleted)
}

function summarizeFieldChangeSettings(
  settings: NotificationFieldChangeSettingsType | null,
  references: ReferenceMaps
) {
  if (!settings?.enabled) return []

  const parts: string[] = []
  const anyFieldNames = settings.anyFieldIdsChanged.map((fieldId) =>
    fieldName(fieldId, references)
  )
  const allFieldNames = settings.allFieldIdsChanged.map((fieldId) =>
    fieldName(fieldId, references)
  )
  const noFieldNames = settings.noFieldIdsChanged.map((fieldId) =>
    fieldName(fieldId, references)
  )

  if (anyFieldNames.length === 1) parts.push(`${anyFieldNames[0]} is changed`)
  else if (anyFieldNames.length > 1)
    parts.push(`at least one of ${joinList(anyFieldNames, 'or')} is changed`)

  if (allFieldNames.length === 1) parts.push(`${allFieldNames[0]} is changed`)
  else if (allFieldNames.length === 2)
    parts.push(`${joinList(allFieldNames, 'and')} are both changed`)
  else if (allFieldNames.length > 2)
    parts.push(`${joinList(allFieldNames, 'and')} are all changed`)

  if (noFieldNames.length === 1)
    parts.push(`${noFieldNames[0]} is left unchanged`)
  else if (noFieldNames.length > 1)
    parts.push(`${joinList(noFieldNames, 'and')} are left unchanged`)

  return parts
}

function statusStateName(
  state: NotificationStatusState,
  references: ReferenceMaps
) {
  if (state.type === 'closed') return 'Closed'
  if (state.type === 'create') return 'No previous status'
  if (state.type === 'draft') return 'Draft'
  return statusName(state.statusId, references)
}

function summarizeTransition(
  prefix: 'from' | 'to',
  transition: NotificationTransition,
  references: ReferenceMaps
) {
  if (transition === 'any_valid') return undefined

  const names = transition.map((state) => statusStateName(state, references))
  return names.length ? `${prefix} ${joinList(names, 'or')}` : undefined
}

function summarizeOptionStateChanged(
  settings: OptionStateChangedTriggerSettingsType,
  references: ReferenceMaps
) {
  const field = references.fields.get(settings.fieldId)
  if (!field || field.settings.type !== 'approvals')
    return `any ${fieldName(settings.fieldId, references)} state is changed`

  const { single } = approvalFieldSettings(field)
  return `${single ? 'the' : 'any'} ${approvalStateLabel(
    settings.fieldId,
    references
  )} state is changed`
}

function summarizeTriggerClause(
  trigger: NotificationTriggerType,
  settings: NotificationTriggerSettingsType | null,
  references: ReferenceMaps
) {
  const { article, clause } = triggerMetadata(trigger)
  const triggerClause = [article, clause].filter(Boolean).join(' ')
  if (!settings) return triggerClause

  if (settings.type === 'advanced' || settings.type === 'reverted') {
    const transitionClause = [
      summarizeTransition('from', settings.from, references),
      summarizeTransition('to', settings.to, references),
    ]
      .filter((part): part is string => !!part)
      .join(' ')
    const eventClause = [triggerClause, transitionClause]
      .filter(Boolean)
      .join(' ')

    return joinList(
      [
        eventClause,
        ...summarizeFieldChangeSettings(
          settings.useAdvancedSettings,
          references
        ),
      ],
      'and'
    )
  }

  if (settings.type === 'option_state_changed')
    return summarizeOptionStateChanged(settings, references)

  if (settings.type === 'row_edited')
    return joinList(
      [triggerClause, ...summarizeFieldChangeSettings(settings, references)],
      'and'
    )

  return assertNever(settings)
}

function summarizeCondition(
  condition: NotificationConditionType,
  references: ReferenceMaps
) {
  switch (condition.type) {
    case 'all_option_states_filled':
      return `all ${approvalStateLabel(
        condition.fieldId,
        references
      )} states are filled`
    case 'any_option_state_empty':
      return `any ${approvalStateLabel(
        condition.fieldId,
        references
      )} state is empty`
    case 'includes_options': {
      const optionNames = condition.optionIds.map((optionId) =>
        fieldOptionLabel({
          fieldId: condition.fieldId,
          optionId,
          references,
        })
      )
      return optionNames.length
        ? `the ${fieldName(
            condition.fieldId,
            references
          )} field contains ${joinList(optionNames, 'or')}`
        : undefined
    }
    case 'status_includes': {
      const statusNames = condition.statuses.map((status) =>
        statusStateName(status, references)
      )
      return statusNames.length
        ? `the status is ${joinList(statusNames, 'or')}`
        : undefined
    }
  }
}

function notificationRuleClauses(
  notification: Pick<
    Notification,
    'conditions' | 'trigger' | 'triggerSettings'
  >,
  referenceData: NotificationSummaryReferenceData
): NotificationRuleClauses {
  const references = toReferenceMaps(referenceData)
  const conditions = notification.conditions.flatMap((condition) => {
    const summary = summarizeCondition(condition, references)
    return summary ? [summary] : []
  })

  return {
    conditions,
    trigger: summarizeTriggerClause(
      notification.trigger,
      notification.triggerSettings,
      references
    ),
  }
}

function formatRuleClauses({ conditions, trigger }: NotificationRuleClauses) {
  const conditionClause = conditions.length
    ? `, but only if ${joinList(conditions, 'and')}`
    : ''

  return `${trigger}${conditionClause}`
}

/**
 * Creates the human-readable "Sent When" table text for a persisted
 * notification.
 *
 * @example
 * const summary = summarizeWhenAndIf(notification, {
 *   fields: projectFields,
 *   statuses: workflowStatuses,
 * })
 * // "A workflow record is edited and Package Number is changed"
 */
export function summarizeWhenAndIf(
  notification: Pick<
    Notification,
    'conditions' | 'trigger' | 'triggerSettings'
  >,
  referenceData: NotificationSummaryReferenceData
) {
  return capitalizeFirst(
    formatRuleClauses(notificationRuleClauses(notification, referenceData))
  )
}

/**
 * Creates the live natural-language summary shown in the notification form.
 *
 * Once a trigger is selected, missing template and recipient selections are
 * omitted so the configured trigger settings and conditions remain visible.
 *
 * @example
 * summarizeNotificationConfiguration({
 *   conditions: [],
 *   recipientCount: 1,
 *   template: { deleted: false, name: 'Record updated' },
 *   trigger: 'row_edited',
 *   triggerSettings: null,
 * }, referenceData)
 * // "Send the “Record updated” template to 1 recipient when a workflow record is edited."
 */
export function summarizeNotificationConfiguration(
  {
    conditions,
    recipientCount,
    template,
    trigger,
    triggerSettings,
  }: NotificationConfigurationSummary,
  referenceData: NotificationSummaryReferenceData
) {
  if (!trigger) return 'Select a trigger to preview this notification.'

  const rule = formatRuleClauses(
    notificationRuleClauses(
      { conditions, trigger, triggerSettings },
      referenceData
    )
  )
  const delivery = template
    ? `Send the “${referenceLabel(template.name, template.deleted)}” template`
    : 'Send email'
  const recipients = recipientCount
    ? ` to ${recipientCount} ${
        recipientCount === 1 ? 'recipient' : 'recipients'
      }`
    : ''

  return `${delivery}${recipients} when ${rule}.`
}

/**
 * Creates the recipients table text for a persisted notification.
 *
 * @example
 * const summary = summarizeRecipients(notification)
 * // "Brett, Team Inbox"
 */
export function summarizeRecipients(notification: Pick<Notification, 'to'>) {
  const labels = notification.to.map((recipient) => {
    if (recipient.__typename === 'User')
      return recipient.displayName ?? recipient.mail
    return recipient.label ?? recipient.address
  })

  return labels.length ? labels.join(', ') : 'No recipients'
}
